import { t as translate } from 'i18next';

import { FeatureFlag, get as getFeatureFlag } from '../FeatureFlagService';
import InAppSalesChatService from '../InAppSalesChatService';
import { logError } from '../LoggingService/LoggingService';
import LegacyZendeskService, {
  defaultZendeskSettings,
  ZENDESK_TICKET_FORM_ID,
} from '../ZendeskService/LegacyZendeskService';
import MessagingZendeskService from '../ZendeskService/MessagingZendeskService';
import { ZendeskApiKeyError, ZendeskSettings } from '../ZendeskService/types';
import { hasZendeskApiInitialized } from '../ZendeskService/utils';

import avatar from './avatar.png';
import { setSupportChatStatus } from './reducer';
import { selectSupportChatStatus } from './selectors';
import { InAppChatService, SupportChatStatus } from './types';

/**
 * @deprecated Use InAppSalesChatService.show or window.(top.)services.inAppSalesChat.show instead.
 */
export const show = InAppSalesChatService.show;
/**
 * @deprecated Use InAppSalesChatService.hide or window.(top.)services.inAppSalesChat.hide instead
 */
export const hide = InAppSalesChatService.hide;

function initializeLegacy() {
  LegacyZendeskService.initialize()
    .then((zendesk) =>
      zendesk('webWidget:on', 'chat:status', (status: SupportChatStatus) => {
        window.store.dispatch(setSupportChatStatus(status));
        if (status === SupportChatStatus.Offline) {
          zendesk('webWidget', 'chat:end');
        }
      }),
    )
    .catch((error) => {
      if (error instanceof ZendeskApiKeyError) {
        // API key not set
        return;
      }

      logError(error);
    });
}

function initializeMessaging() {
  MessagingZendeskService.initialize().catch((error) => {
    if (error instanceof ZendeskApiKeyError) {
      // API key not set
      return;
    }

    logError(error);
  });
}

function initialize() {
  const hasZendeskMessagingEnabled = getFeatureFlag(FeatureFlag.EnableZendeskMessaging);

  if (hasZendeskMessagingEnabled) {
    initializeMessaging();
  } else {
    initializeLegacy();
  }
}

function openContactForm() {
  if (!hasZendeskApiInitialized(window)) {
    return;
  }

  LegacyZendeskService.zendesk(
    'webWidget',
    'updateSettings',
    Object.assign<{}, ZendeskSettings, Partial<ZendeskSettings>>({}, window.zESettings ?? defaultZendeskSettings, {
      webWidget: {
        chat: {
          suppress: true,
        },
        contactForm: {
          suppress: false,
          ticketForms: [{ id: ZENDESK_TICKET_FORM_ID }],
        },
        helpCenter: {
          suppress: true,
        },
      },
    }),
  );

  LegacyZendeskService.zendesk('webWidget', 'show');
  LegacyZendeskService.zendesk('webWidget', 'open');
}

function openSupportChat() {
  if (!hasZendeskApiInitialized(window)) {
    return;
  }

  LegacyZendeskService.zendesk(
    'webWidget',
    'updateSettings',
    Object.assign<{}, ZendeskSettings, Partial<ZendeskSettings>>({}, window.zESettings ?? defaultZendeskSettings, {
      webWidget: {
        chat: {
          suppress: false,
          concierge: {
            avatarPath: avatar,
            name: translate('chatInformation.name'),
            title: { '*': translate('chatInformation.support') },
          },
        },
        contactForm: {
          suppress: true,
        },
        helpCenter: {
          suppress: true,
        },
      },
    }),
  );

  LegacyZendeskService.zendesk('webWidget', 'show');
  LegacyZendeskService.zendesk('webWidget', 'open');
}

function openLegacy() {
  const supportChatStatus = selectSupportChatStatus(window.store.getState());

  if (supportChatStatus === SupportChatStatus.Offline) {
    openContactForm();
  } else {
    openSupportChat();
  }
}

function openMessaging() {
  if (!hasZendeskApiInitialized(window)) {
    return;
  }

  MessagingZendeskService.zendesk('messenger', 'show');
  MessagingZendeskService.zendesk('messenger', 'open');
}

function open() {
  const hasZendeskMessagingEnabled = getFeatureFlag(FeatureFlag.EnableZendeskMessaging);

  if (hasZendeskMessagingEnabled) {
    openMessaging();
  } else {
    openLegacy();
  }
}

const ZendeskInAppChatService: InAppChatService = {
  initialize,
  open,
  show,
  hide,
};

export default ZendeskInAppChatService;
